import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import {
  Lock,
  KeyRound,
  Shield,
  CheckCircle2,
  AlertCircle,
  User,
  Check,
  RefreshCw,
  ArrowRight,
  ShieldCheck,
  Sparkles
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import swal from "sweetalert";
import ApiClient from "../../../methods/api/apiClient";
import loader from "../../../methods/loader";
import methodModel from "../../../methods/methods";
import Layout from "../../../components/global/layout";
import FormControl from "../../../components/common/FormControl";

export default function ChangePassword() {
  const history = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  // Super Admin Profile State (from user/admin-profile)
  const [adminProfile, setAdminProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  // Form State
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Validation & Error State matching Login page
  const [errors, setErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [loading, setLoading] = useState(false);

  // Fetch Super Admin Profile from user/admin-profile
  useEffect(() => {
    fetchAdminProfile();
  }, []);

  const fetchAdminProfile = async () => {
    setProfileLoading(true);
    try {
      const res = await ApiClient.get("user/admin-profile");
      if (res?.success && (res?.data || res?.user_data)) {
        setAdminProfile(res.data || res.user_data);
      }
    } catch (err) {
      console.error("Failed to fetch super admin profile:", err);
    } finally {
      setProfileLoading(false);
    }
  };

  // Real-time Current Password Validation Handler
  const handleCurrentPasswordChange = (val) => {
    setCurrentPassword(val);
    if (!val) {
      setErrors((prev) => ({ ...prev, currentPassword: "Please enter your current password." }));
    } else {
      setErrors((prev) => ({ ...prev, currentPassword: "" }));
    }
  };

  // Real-time New Password Validation Handler matching Login page
  const handleNewPasswordChange = (val) => {
    setNewPassword(val);
    if (!val) {
      setErrors((prev) => ({ ...prev, newPassword: "Please enter your new password." }));
    } else {
      const check = methodModel.validateLoginPassword(val);
      if (!check.success) {
        setErrors((prev) => ({ ...prev, newPassword: check.message }));
      } else if (currentPassword && val === currentPassword) {
        setErrors((prev) => ({
          ...prev,
          newPassword: "New password cannot be the same as your current password."
        }));
      } else {
        setErrors((prev) => ({ ...prev, newPassword: "" }));
      }
    }

    // Re-check confirm password if already typed
    if (confirmPassword) {
      if (confirmPassword !== val) {
        setErrors((prev) => ({ ...prev, confirmPassword: "Passwords do not match." }));
      } else {
        setErrors((prev) => ({ ...prev, confirmPassword: "" }));
      }
    }
  };

  // Real-time Confirm Password Validation Handler
  const handleConfirmPasswordChange = (val) => {
    setConfirmPassword(val);
    if (!val) {
      setErrors((prev) => ({ ...prev, confirmPassword: "Please confirm your new password." }));
    } else if (val !== newPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: "Passwords do not match." }));
    } else {
      setErrors((prev) => ({ ...prev, confirmPassword: "" }));
    }
  };

  // Password Requirement Checks
  const criteria = {
    length: newPassword.length >= 8 && newPassword.length <= 32,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(newPassword)
  };

  // Form Submit Handler
  const handleSubmit = (e) => {
    e.preventDefault();

    let newErrors = {
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    };
    let hasError = false;

    // Validate current password
    if (!currentPassword) {
      newErrors.currentPassword = "Please enter your current password.";
      hasError = true;
    }

    // Validate new password with same validator as Login page
    const passwordCheck = methodModel.validateLoginPassword(newPassword);
    if (!passwordCheck.success) {
      newErrors.newPassword = passwordCheck.message;
      hasError = true;
    } else if (currentPassword && newPassword === currentPassword) {
      newErrors.newPassword = "New password cannot be the same as your current password.";
      hasError = true;
    }

    // Validate confirm password
    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password.";
      hasError = true;
    } else if (confirmPassword !== newPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    setErrors({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setLoading(true);
    loader(true);

    const payload = {
      currentPassword,
      newPassword,
      confirmPassword
    };

    // Call user/admin/change-password endpoint via PUT
    ApiClient.put("user/admin/change-password", payload)
      .then((res) => {
        loader(false);
        setLoading(false);

        if (res?.success) {
          swal({
            title: "Password Updated",
            text: res?.message || "Your administrator password has been updated successfully.",
            icon: "success",
            button: "Done",
            className: "custom-swal"
          });
          // Reset form fields
          setCurrentPassword("");
          setNewPassword("");
          setConfirmPassword("");
        } else {
          const errMsg =
            res?.error?.message ||
            res?.message ||
            "Failed to change password. Please verify your current password.";
          if (errMsg.toLowerCase().includes("current password")) {
            setErrors((prev) => ({ ...prev, currentPassword: errMsg }));
          }
          toast.error(errMsg);
        }
      })
      .catch((err) => {
        loader(false);
        setLoading(false);
        const errMsg =
          err?.response?.data?.error?.message ||
          err?.message ||
          "Failed to update password. Please check your current password and try again.";
        toast.error(errMsg);
      });
  };

  const displayName =
    adminProfile?.fullName ||
    (adminProfile?.firstName ? `${adminProfile.firstName} ${adminProfile.lastName || ""}` : "") ||
    user?.fullName ||
    "Super Admin";

  const displayEmail = adminProfile?.email || user?.email || "superadmin@yopmail.com";
  const displayRole = adminProfile?.role?.name || user?.role?.name || "Super Admin";

  return (
    <Layout>
      <div className="w-full max-w-full overflow-x-hidden space-y-6 pb-12 antialiased">

        {/* ======================================================== */}
        {/* 1. TOP HEADER & BREADCRUMB NAVIGATION */}
        {/* ======================================================== */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-2 border-b border-slate-200">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 mb-1">
              <Link to="/dashboard" className="hover:text-[#115B9D] transition-colors">
                Dashboard
              </Link>
              <span>/</span>
              <Link to="/profile" className="hover:text-[#115B9D] transition-colors">
                Profile
              </Link>
              <span>/</span>
              <span className="text-slate-800 font-bold">Security & Password</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight flex items-center gap-2.5">
              <KeyRound className="w-7 h-7 text-[#115B9D]" />
              Change Password
            </h1>
            <p className="text-xs sm:text-sm text-slate-600 font-normal mt-0.5">
              Update your administrator credentials with enterprise cryptographic verification.
            </p>
          </div>

          <div className="flex items-center gap-2.5 self-stretch sm:self-auto">
            <Link
              to="/profile"
              className="px-4 py-2 rounded-xl text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-all border border-slate-200 cursor-pointer text-center"
            >
              Back to Profile
            </Link>
          </div>
        </div>

        {/* ======================================================== */}
        {/* 2. SUPER ADMIN PROFILE OVERVIEW TILE (user/admin-profile) */}
        {/* ======================================================== */}
        <div className="bg-gradient-to-br from-[#071324] via-[#0E2C52] to-[#115B9D] rounded-2xl p-5 sm:p-6 text-white shadow-md border border-slate-800 relative overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-sky-400/15 rounded-full blur-3xl pointer-events-none" />

          <div className="flex items-center gap-4 relative z-10">
            <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-sky-200 shadow-inner shrink-0">
              <ShieldCheck className="w-8 h-8 text-sky-300" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-base sm:text-lg font-black text-white tracking-tight">
                  {displayName}
                </span>
                <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-[10px] font-black uppercase tracking-wider text-emerald-300 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  {displayRole}
                </span>
              </div>
              <p className="text-xs text-sky-100/90 mt-0.5 font-mono">{displayEmail}</p>
              <p className="text-[11px] text-sky-200/75 mt-0.5">
                Authenticated Super Admin Session &bull; Password protection active
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-stretch md:self-auto justify-end relative z-10 pt-2 md:pt-0 border-t md:border-t-0 border-white/10">
            <button
              onClick={fetchAdminProfile}
              disabled={profileLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition-all border border-white/15 cursor-pointer"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${profileLoading ? "animate-spin" : ""}`} />
              <span>{profileLoading ? "Refreshing..." : "Sync Profile"}</span>
            </button>
          </div>
        </div>

        {/* ======================================================== */}
        {/* 3. MAIN FORM & SECURITY GUIDELINES GRID */}
        {/* ======================================================== */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

          {/* Form Card (7 Cols) */}
          <div className="lg:col-span-7 bg-white rounded-2xl p-5 sm:p-7 border border-slate-200/90 shadow-[0_2px_10px_rgba(0,0,0,0.03)] space-y-6">
            <div className="border-b border-slate-100 pb-4">
              <h2 className="text-lg font-black text-slate-900 tracking-tight flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#115B9D]" />
                Update Administrator Credentials
              </h2>
              <p className="text-xs text-slate-600 font-medium mt-0.5">
                Please enter your existing password to authorize setting a new password.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate className="space-y-4">

              {/* Platform FormControl: Current Password */}
              <FormControl
                type="password"
                name="currentPassword"
                id="adminCurrentPassword"
                label="Current Password"
                required={true}
                showEye={true}
                placeholder="Enter current password"
                value={currentPassword}
                error={errors.currentPassword}
                onChange={handleCurrentPasswordChange}
                autoComplete="current-password"
                divClass="space-y-1"
                labelClass="!text-xs !font-bold !uppercase !tracking-wider !text-slate-900 !mb-1.5"
                className="!h-10 sm:!h-11 !rounded-xl text-slate-900 text-sm shadow-none"
              />

              {/* Platform FormControl: New Password */}
              <FormControl
                type="password"
                name="newPassword"
                id="adminNewPassword"
                label="New Password"
                required={true}
                showEye={true}
                placeholder="Enter new password (min. 8 characters)"
                value={newPassword}
                error={errors.newPassword}
                onChange={handleNewPasswordChange}
                autoComplete="new-password"
                divClass="space-y-1"
                labelClass="!text-xs !font-bold !uppercase !tracking-wider !text-slate-900 !mb-1.5"
                className="!h-10 sm:!h-11 !rounded-xl text-slate-900 text-sm shadow-none"
              />

              {/* Platform FormControl: Confirm New Password */}
              <FormControl
                type="password"
                name="confirmPassword"
                id="adminConfirmPassword"
                label="Confirm New Password"
                required={true}
                showEye={true}
                placeholder="Re-enter your new password"
                value={confirmPassword}
                error={errors.confirmPassword}
                onChange={handleConfirmPasswordChange}
                autoComplete="new-password"
                divClass="space-y-1"
                labelClass="!text-xs !font-bold !uppercase !tracking-wider !text-slate-900 !mb-1.5"
                className="!h-10 sm:!h-11 !rounded-xl text-slate-900 text-sm shadow-none"
              />

              {/* Action Buttons */}
              <div className="pt-3 flex flex-col sm:flex-row items-center gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-6 h-11 rounded-xl bg-gradient-to-r from-[#115B9D] via-[#0E528F] to-[#0A3E6E] hover:from-[#0E528F] hover:to-[#08335C] text-white font-bold text-sm shadow-md shadow-[#115B9D]/20 hover:shadow-lg hover:shadow-[#115B9D]/30 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-75 disabled:cursor-not-allowed"
                >
                  <KeyRound className="w-4 h-4" />
                  <span>{loading ? "Updating Password..." : "Update Administrator Password"}</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                    setErrors({ currentPassword: "", newPassword: "", confirmPassword: "" });
                  }}
                  className="w-full sm:w-auto px-5 h-11 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm transition-all border border-slate-200 cursor-pointer"
                >
                  Reset Form
                </button>
              </div>

            </form>
          </div>

          {/* Security & Password Guidelines Side Panel (5 Cols) */}
          <div className="lg:col-span-5 space-y-5">

            {/* Live Password Strength Criteria Checklist */}
            <div className="bg-white rounded-2xl p-5 border border-slate-200/90 shadow-[0_2px_10px_rgba(0,0,0,0.03)] space-y-4">
              <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                <Sparkles className="w-4 h-4 text-[#115B9D]" />
                <h3 className="text-sm font-black text-slate-900 tracking-tight">
                  Password Strength Requirements
                </h3>
              </div>
              <p className="text-xs text-slate-600 font-medium">
                To guarantee account integrity, passwords must satisfy these enterprise security rules:
              </p>

              <div className="space-y-2.5">
                <div className="flex items-center gap-2.5 text-xs font-semibold">
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                      criteria.length ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    <Check className="w-3 h-3 stroke-[3]" />
                  </div>
                  <span className={criteria.length ? "text-emerald-900" : "text-slate-600"}>
                    8 to 32 characters in length
                  </span>
                </div>

                <div className="flex items-center gap-2.5 text-xs font-semibold">
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                      criteria.uppercase ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    <Check className="w-3 h-3 stroke-[3]" />
                  </div>
                  <span className={criteria.uppercase ? "text-emerald-900" : "text-slate-600"}>
                    At least one uppercase letter (A-Z)
                  </span>
                </div>

                <div className="flex items-center gap-2.5 text-xs font-semibold">
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                      criteria.lowercase ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    <Check className="w-3 h-3 stroke-[3]" />
                  </div>
                  <span className={criteria.lowercase ? "text-emerald-900" : "text-slate-600"}>
                    At least one lowercase letter (a-z)
                  </span>
                </div>

                <div className="flex items-center gap-2.5 text-xs font-semibold">
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                      criteria.number ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    <Check className="w-3 h-3 stroke-[3]" />
                  </div>
                  <span className={criteria.number ? "text-emerald-900" : "text-slate-600"}>
                    At least one numeric digit (0-9)
                  </span>
                </div>

                <div className="flex items-center gap-2.5 text-xs font-semibold">
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                      criteria.special ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    <Check className="w-3 h-3 stroke-[3]" />
                  </div>
                  <span className={criteria.special ? "text-emerald-900" : "text-slate-600"}>
                    At least one special character (!@#$%^&*...)
                  </span>
                </div>
              </div>
            </div>

            {/* Security Tip Tile */}
            <div className="bg-sky-50/80 rounded-2xl p-5 border border-sky-100 text-xs text-sky-950 space-y-2">
              <div className="font-bold text-sky-900 flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#115B9D]" />
                Security Advisory
              </div>
              <p className="leading-relaxed text-sky-900 font-medium">
                Changing your password takes effect immediately across all active platform sessions. Avoid re-using passwords from external services.
              </p>
            </div>

          </div>

        </div>

      </div>
      <ToastContainer position="top-right" />
    </Layout>
  );
}
